import {
	CustomFieldDto,
	ProviderDto,
	FamilyDto,
	ChildDto,
	EngagementDto,
	DefaultFieldTypeStatusEnum,
	FieldDto,
	EngagementStatusDto,
} from '@families-link/shared';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMuiModal } from '../../../_core/hooks';
import dayjs from 'dayjs';
import {
	EngagementBulkEditFormModal,
	EngagementBulkEditFormModalProps,
} from '../../components/modal-form/modal-bulk-edit-form.component';

export type useOpenModalFormHookProps = {
	isCreate: boolean;
	districtId?: string | null;
	defaultValuesArray?: any;
	submitButtonName?: string;
	toastText?: string;
	fieldsListFromDistrict?: CustomFieldDto[];
	providersList?: ProviderDto[];
	familiesList?: FamilyDto[];
	childrenList?: ChildDto[];
	statusesList?: EngagementStatusDto[];
	featchAction(values: any): Promise<void>;
	handlePagination?(): void;
};

export function useOpenModalFormBulkEdit(props: useOpenModalFormHookProps) {
	const {
		featchAction,
		handlePagination,
		submitButtonName,
		toastText,
		fieldsListFromDistrict,
		providersList,
		familiesList,
		childrenList,
		districtId,
		statusesList,
	} = props;

	const transformEngagementsForForm = (engagements: EngagementDto[]) =>
		engagements.map((engagement) => {
			const fieldsList = engagement.customFields;
			const fieldDuration = fieldsList?.find((el) => el.name === 'Duration')?._id;
			const fieldType = fieldsList?.find((el) => el.name === 'Type')?._id;

			const valueType = engagement.customFieldsValues.find(
				(item) => item.fieldId.toString() === fieldType?.toString()
			)?.value;

			const transformedFields = engagement.customFieldsValues.reduce((result: any, el) => {
				result[`field_${el.fieldId}`] = el.value;
				return result;
			}, {});

			const day =
				valueType === DefaultFieldTypeStatusEnum.Appointment
					? engagement.duration?.startDate
						? dayjs(engagement.duration?.startDate)
						: null
					: null;
			const time =
				valueType === DefaultFieldTypeStatusEnum.Appointment
					? engagement.duration?.startDate
						? dayjs(engagement.duration?.startDate)
						: null
					: null;
			const appointmentDuration =
				valueType === DefaultFieldTypeStatusEnum.Appointment
					? engagement.duration?.startDate && engagement.duration?.endDate
						? dayjs(engagement.duration?.endDate).diff(engagement.duration?.startDate, 'millisecond')
						: ''
					: '';
			const duration = [engagement.duration?.startDate, engagement.duration?.endDate];

			return { ...engagement, duration, day, time, appointmentDuration, ...transformedFields };
		});

	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const { openModal, closeModal, updateCurrentModal } =
		useMuiModal<EngagementBulkEditFormModalProps>(EngagementBulkEditFormModal);

	const showToast = (message: string, variant: 'error' | 'default' | 'success' | 'warning' | 'info') => {
		enqueueSnackbar(message, { variant });
	};

	const handleSubmit = async (values: any): Promise<void> => {
		try {
			await featchAction(values);
			showToast(toastText ?? 'Successfully performed', 'success');

			if (handlePagination) {
				handlePagination();
			}
			closeModal();
		} catch (error) {
			updateCurrentModal({ formError: error });
		}
	};

	const handleOpenModal = useCallback(
		(engagements: EngagementDto[]) => {
			openModal({
				formTitle: t('engagements.forms.bulk-edit-engagements'),
				defaultValuesArray: transformEngagementsForForm(engagements),
				submitAction: handleSubmit,
				closeModal,
				submitButtonName,
				statusesList,
				generateFileUrlFunction: undefined,
				formError: undefined,
			});
		},
		[openModal, handleSubmit]
	);

	return handleOpenModal;
}
